import { GamePack } from '@/API';
import { createSession } from '@/client/session';
import { PromptExtended } from '@/components/GamePacks/Prompts/PromptList';
import GameSidebar from '@/components/Host/Setup/GameSidebar';
import ReloadIcon from '@/components/Icons/ReloadIcon';
import { useAuth } from '@/providers/AuthProvider';
import { guideGamePacks, guideGamePacksNames, listGamePackHost } from '@/utils/gamePacks';
import { DropResult } from '@hello-pangea/dnd';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MAX_SELECTED_PROMPTS } from '../host/GameSetup';
import welcomeImage from '@/assets/images/welcome.png';
import ReactGA from 'react-ga4';

// Main component
export default function Guide() {
  const [gamePacks, setGamePacks] = useState<GamePack[]>([]);
  const [activeGamePack, setActiveGamePack] = useState<GamePack | null>();
  const [quickGameSelected, setQuickGameSelected] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0);
  const [showAllPrompts, setShowAllPrompts] = useState<boolean>(false);
  const [availablePrompts, setAvailablePrompts] = useState<PromptExtended[]>([]);
  const [selectedPrompts, setSelectedPrompts] = useState<PromptExtended[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const auth = useAuth();

  const tenantId = auth.groups?.find((group) => group.startsWith('tenant:'))?.split(':')[1];

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId) return;
      const response = await listGamePackHost();

      const gamePacks = guideGamePacks.map((guideGamePack) => {
        return (
          (response.data.gamePackList as GamePack[]).find(
            (gamePack) => gamePack.name === guideGamePack
          ) ?? response.data.gamePackList[0]
        );
      });

      setGamePacks(gamePacks);
    };

    fetchData();
  }, [tenantId]);

  useEffect(() => {
    const newAvailablePrompts = gamePacks
      .filter((gamePack) => activeGamePack?.id === gamePack.id)
      .flatMap((gamePack) =>
        (gamePack.prompts ?? []).map((item) => ({
          ...item,
          gamePack: gamePack
        }))
      );
    setAvailablePrompts(newAvailablePrompts as PromptExtended[]);
  }, [activeGamePack, gamePacks, selectedPrompts]);

  useEffect(() => {
    if (selectedPrompts.length > 0) {
      setStep(4);
    } else if (step === 4) {
      setStep(3);
    }
  }, [selectedPrompts.length]);

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    // Condition: Dragging within the same list
    const resultPrompts = Array.from(selectedPrompts);
    const [removed] = resultPrompts.splice(source.index, 1);
    resultPrompts.splice(destination.index, 0, removed);

    setSelectedPrompts(resultPrompts);
  };

  const handleSelectGamePack = (gamePack?: GamePack) => {
    setActiveGamePack(gamePack);
    setStep(2);
  };

  const handleClear = () => {
    setSelectedPrompts([]);
  };

  const launchGame = async () => {
    if (selectedPrompts.length > 0) {
      setLoading(true);
      ReactGA.event({
        category: 'First-time user flow',
        action: 'Clicked Button',
        label: 'Start Game'
      });
      try {
        const result = await createSession({
          promptIds: selectedPrompts.map((p) => p.id)
        });
        navigate(`/session/${result?.id}/host`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleNext = () => {
    if (quickGameSelected) {
      ReactGA.event({
        category: 'First-time user flow',
        action: 'Clicked Button',
        label: 'Surprise me - Start Game'
      });
      navigate('/quick-game?guide');
    } else {
      setStep(3);
    }
  };

  const handleSkip = () => {
    ReactGA.event({
      category: 'First-time user flow',
      action: 'Clicked Link',
      label: 'Skip & Explore'
    });
    navigate('/dashboard/host');
  };

  return (
    <>
      <div className="mx-32 relative top-0 w-auto">
        <div
          className={clsx(
            'absolute h-4 bg-primary-500 -top-60',
            step === 0 && 'w-0',
            step === 1 && 'w-1/4',
            step === 2 && 'w-2/4',
            step === 3 && 'w-3/4',
            step === 4 && 'w-full'
          )}
        ></div>
      </div>
      {step == 0 && (
        <div className="pt-32 pb-64 px-100 bg-white z-100 rounded-xs relative w-608 m-auto flex flex-col items-center">
          <img src={welcomeImage} className="w-178 text-center" />
          <h2 className="text-h2 my-14 text-center">
            Welcome, {auth.userAttributes?.given_name}! <br />
          </h2>
          <h3 className="text-h3 text-center mb-24">
            You’re a few clicks away from running your very first game of Scribl
          </h3>
          <div className="flex flex-col gap-16">
            <div>
              <h3 className="text-h3 opacity-80">Set Up Your Game</h3>
              <p className="text-body text-neutral-400">
                We’ll guide you through choosing prompts and tailoring the session to your team.
              </p>
            </div>
            <div>
              <h3 className="text-h3 opacity-80">Share & Start</h3>
              <p className="text-body text-neutral-400">
                When you’re ready, launch the game and share your screen. Everyone joins in with a
                QR code scan on their smartphones.
              </p>
            </div>
            <div>
              <h3 className="text-h3 opacity-80">Let’s Play!</h3>
              <p className="text-body text-neutral-400">
                Your team will take turns sharing their art and stories to help spark connection,
                boost creativity, and have fun.
              </p>
            </div>
          </div>
          <button className="btn btn-primary mt-20 w-full" onClick={() => setStep(1)}>
            Let’s Go
          </button>
        </div>
      )}
      {step > 0 && step < 3 && (
        <div className="py-32 px-40 bg-white z-100 rounded-xs relative w-608 m-auto">
          <h2 className="text-h2 text-center mb-12">
            What’s the goal for your
            <br /> team’s first Scribl session?
          </h2>
          <h3 className="text-h3 text-center mb-24">Start with a popular theme</h3>
          <div className="flex gap-16 flex-wrap">
            {gamePacks.map((gamePack) => (
              <div
                className={clsx(
                  'w-256 h-72 border border-neutral-200 rounded-xs flex items-center justify-center cursor-pointer',
                  activeGamePack?.id === gamePack.id && 'border-2 border-primary-500 bg-primary-100'
                )}
                onClick={() => {
                  setQuickGameSelected(false);
                  handleSelectGamePack(gamePack);
                }}
                key={gamePack.id}
              >
                <p className="font-semibold text-16/20">
                  {guideGamePacksNames[gamePack.name].name}
                </p>
              </div>
            ))}
          </div>
          <p className="text-body-strong text-center py-24">OR</p>
          <div
            className={clsx(
              'w-full h-48 border border-neutral-200 rounded-xs flex items-center justify-center mb-24 cursor-pointer',
              quickGameSelected && 'border-2 border-primary-500 bg-primary-100'
            )}
            onClick={() => {
              ReactGA.event({
                category: 'First-time user flow',
                action: 'Clicked Option',
                label: 'Surprise me'
              });
              setQuickGameSelected(true);
              handleSelectGamePack();
            }}
          >
            <p className="font-semibold text-16/20">Surprise me and create a game!</p>
          </div>
          <button
            className="btn btn-primary mb-24 w-full"
            disabled={!activeGamePack && !quickGameSelected}
            onClick={handleNext}
          >
            Next
          </button>
          <p className="text-body-sm text-center text-neutral-400">
            Not ready to play yet?{' '}
            <button className="link link-sm" onClick={handleSkip}>
              Skip and Explore
            </button>
          </p>
        </div>
      )}
      {step >= 3 && (
        <div className="flex gap-32 relative m-auto justify-center items-start">
          <div className="py-32 px-40 bg-white z-100 rounded-xs relative h-632 w-608 shrink-0">
            <div className="h-516 overflow-auto scrollbar-hidden mb-24">
              {activeGamePack && (
                <h2 className="text-h2 text-center mb-12">
                  {guideGamePacksNames[activeGamePack.name].title}
                </h2>
              )}
              <p className="text-body text-neutral-400 text-center mb-24">
                Pick a prompt or two that you think will spark connection and boost creativity!
              </p>
              {activeGamePack?.prompts && (
                <>
                  <div className="flex gap-16 flex-wrap">
                    {(showAllPrompts ? availablePrompts : availablePrompts.slice(0, 8)).map(
                      (prompt) => {
                        const selected = selectedPrompts.some(
                          (selectedPrompt) => selectedPrompt.id === prompt.id
                        );

                        const disabled = selectedPrompts.length >= MAX_SELECTED_PROMPTS;

                        return (
                          <div
                            className={clsx(
                              'w-256 h-72 rounded-xs flex items-center p-12',
                              selected
                                ? 'border-2 border-primary-500 bg-primary-100 hover:cursor-pointer'
                                : disabled
                                  ? 'bg-neutral-100 border-1 border-neutral-200 hover:cursor-not-allowed'
                                  : 'bg-white border-1 border-neutral-200 cursor-pointer'
                            )}
                            onClick={() => {
                              if (disabled && !selected) return;

                              if (selected) {
                                setSelectedPrompts(
                                  selectedPrompts.filter(
                                    (selectedPrompt) => selectedPrompt.id !== prompt.id
                                  )
                                );
                                return;
                              }
                              setSelectedPrompts((prev) => [...prev, prompt]);
                            }}
                            key={prompt.id}
                          >
                            <p className="font-semibold text-16/20">{prompt.text}</p>
                          </div>
                        );
                      }
                    )}
                  </div>
                  {activeGamePack?.prompts?.length > 8 && !showAllPrompts && (
                    <button
                      className="btn btn-neutral mt-16 w-full"
                      onClick={() => setShowAllPrompts(true)}
                    >
                      <ReloadIcon className="w-24 h-24 mr-8" />
                      Show More
                    </button>
                  )}
                </>
              )}
            </div>
            <p className="text-body-sm text-center text-neutral-400">
              Want more options?{' '}
              <Link className="link link-sm" to={`/dashboard/host?gamepack=${activeGamePack?.id}`}>
                Explore All Prompts
              </Link>
            </p>
          </div>
          <GameSidebar
            onDragEnd={onDragEnd}
            clear={handleClear}
            selectedPrompts={selectedPrompts}
            setSelectedPrompts={setSelectedPrompts}
            launchGame={launchGame}
            loading={loading}
            keepTip={true}
          />
        </div>
      )}
    </>
  );
}
